import axios from "axios";
import { toast } from "sonner";
import type { ICreateDocumentRequest } from "@/types/document.types";
import {
  GATHER_TIMEOUT_MS,
  SFDT_CONVERT_TIMEOUT_MS,
  TEXT_UPLOAD_TIMEOUT_MS,
  withTimeout,
} from "@/utils/timeout";

const UPLOAD_FAIL_TITLE = "Couldn't save the document text";
const UPLOAD_FAIL_DESCRIPTION =
  "The document was created, but its text did not upload. Reviews and search may come back empty until you edit the document.";

function toastUploadFailed(): void {
  toast.error(UPLOAD_FAIL_TITLE, { description: UPLOAD_FAIL_DESCRIPTION });
}

/**
 * Bare axios on purpose: upload_text_url is a pre-signed URL and must not
 * carry the session Authorization header (S3 would reject the signature).
 */
async function putPlainText(url: string, plainText: string): Promise<void> {
  await axios.put(url, plainText, {
    headers: { "Content-Type": "text/plain" },
    timeout: TEXT_UPLOAD_TIMEOUT_MS,
  });
}

async function tryUploadOnce(documentId: string, plainText: string): Promise<"ok" | "missing-url"> {
  const { getDocument } = await import("@/services/document.service");
  const newDocument = await getDocument(documentId);
  const uploadUrl = newDocument.upload_text_url;
  if (uploadUrl == null || uploadUrl === "") {
    return "missing-url";
  }
  await putPlainText(uploadUrl, plainText);
  return "ok";
}

/** Load the new document only when its body actually landed, or when there was no body to upload. */
export function shouldLoadCreatedDocument(plainText: string | null, uploaded: boolean): boolean {
  if (plainText == null) return true;
  return uploaded;
}

/**
 * Upload the Word body to the new document's pre-signed URL.
 * Retries once. Toasts if the URL is missing or both attempts fail.
 * Returns whether the body actually landed.
 */
export async function uploadCreatedDocumentText(
  documentId: string,
  plainText: string
): Promise<boolean> {
  try {
    if ((await tryUploadOnce(documentId, plainText)) === "ok") return true;
    if ((await tryUploadOnce(documentId, plainText)) === "ok") return true;
    toastUploadFailed();
    return false;
  } catch (firstError) {
    console.error("Failed to upload text content, retrying once:", firstError);
    try {
      if ((await tryUploadOnce(documentId, plainText)) === "ok") return true;
      toastUploadFailed();
      return false;
    } catch (error) {
      console.error("Failed to upload text content:", error);
      toastUploadFailed();
      return false;
    }
  }
}

export async function gatherCreateDocumentPayload(
  userId: string
): Promise<{ payload: ICreateDocumentRequest; plainText: string | null }> {
  const { getDocumentInfo } = await import("@/utils/document.utils");
  const { getDocumentFile, getDocumentText } = await import("@/taskpane/taskpane");
  const { convertDocxToSfdt } = await import("@/services/document.service");

  const [docInfo, plainText] = await Promise.all([getDocumentInfo(), getDocumentText()]);
  const docxBlob = await getDocumentFile().catch(() => null);
  let sfdtContent = "";
  if (docxBlob != null) {
    try {
      sfdtContent = await withTimeout(
        convertDocxToSfdt(docxBlob),
        SFDT_CONVERT_TIMEOUT_MS,
        "SFDT conversion timed out"
      );
    } catch (error) {
      console.error("SFDT conversion failed; creating document without editor content", error);
    }
  }

  return {
    payload: {
      editor_id: userId,
      title: docInfo.name,
      content: sfdtContent,
      collaborators: [userId],
      doc_type: "ms_word",
      ms_word_id: docInfo.id,
    },
    plainText,
  };
}

async function fallbackCreateDocumentPayload(
  userId: string
): Promise<{ payload: ICreateDocumentRequest; plainText: string | null }> {
  const { getDocumentInfo } = await import("@/utils/document.utils");
  const docInfo = await getDocumentInfo();
  const msWordId = docInfo.id;
  if (msWordId == null || msWordId === "") {
    throw new Error("Missing Word document id");
  }
  return {
    payload: {
      editor_id: userId,
      title: docInfo.name,
      content: "",
      collaborators: [userId],
      doc_type: "ms_word",
      ms_word_id: msWordId,
    },
    plainText: null,
  };
}

/**
 * Never block document create on Word/SFDT hangs. A linked document with
 * empty editor content still unlocks Discuss/Compass/Review API calls.
 */
export async function buildCreateDocumentPayload(
  userId: string
): Promise<{ payload: ICreateDocumentRequest; plainText: string | null }> {
  try {
    return await withTimeout(
      gatherCreateDocumentPayload(userId),
      GATHER_TIMEOUT_MS,
      "gatherCreateDocumentPayload timed out"
    );
  } catch (error) {
    console.error("Failed to gather Word content; creating document without body", error);
    return fallbackCreateDocumentPayload(userId);
  }
}
